import { ImageData, ImageRef, SceneAction, SceneData, SceneRef } from '../../../shared/models';
import { cloneDeep } from '../../../shared/utils/clone';
import { deriveSlug, generateEntityId, makeUniqueSlug } from '../../../shared/utils/slug';
import { splitSlugsFor } from './taken-slugs';

/**
 * Pure clone helpers shared by the editor's copy flows. Kept free of any
 * injected state so the same functions compose for a future chapter copy,
 * which clones many docs in one pass.
 */

/** Slugs of every action already present in the destination scene. */
export function takenActionSlugs(actions: SceneAction[] | undefined): Set<string> {
	return new Set((actions ?? []).map((a) => a.slug).filter((s): s is string => !!s));
}

/**
 * Only the identity changes — SEEK targets (`data.scene` / `data.chapter`) are
 * carried verbatim, so an unresolvable one surfaces as "(missing)" in the
 * form's dropdown rather than being silently rewritten.
 */
export function cloneActionForCopy(source: SceneAction, taken: Set<string>): SceneAction {
	const clone = cloneDeep(source);
	const base = source.slug || (typeof source.type === 'string' ? source.type.toLowerCase() : '') || 'action';
	clone.id = generateEntityId();
	clone.slug = makeUniqueSlug(deriveSlug(base) || 'action', taken);
	return clone;
}

/** True when a SEEK's target chapter is implicit, so copying it across chapters changes its meaning. */
export function isChapterRelativeSeek(action: SceneAction): boolean {
	return action.type === 'SEEK' && !action.data?.chapter;
}

/**
 * Picks a free slug for a scene ref. A split ref also needs every derived
 * `slug__i` free, so a candidate whose family overlaps is rejected and the
 * search continues.
 */
export function uniqueSceneSlug(sourceSlug: string, split: number | undefined, taken: Set<string>): string {
	const base = deriveSlug(sourceSlug) || 'scene';
	const probe = new Set(taken);
	for (;;) {
		const candidate = makeUniqueSlug(base, probe);
		const family = splitSlugsFor({ slug: candidate, split });
		if (!family.some((slug) => taken.has(slug))) return candidate;
		// Block this candidate so the next `makeUniqueSlug` moves past it.
		probe.add(candidate);
	}
}

/** Fresh `id` on every nested content item and action — two entities must never share one. */
function restampNestedIds(doc: SceneData | ImageData): void {
	for (const item of doc.content ?? []) item.id = generateEntityId();
	for (const action of (doc as SceneData).actions ?? []) action.id = generateEntityId();
}

export function cloneSceneDocForCopy(source: SceneData, newSlug: string, newId: string): SceneData {
	const clone = cloneDeep(source);
	clone.id = newId;
	clone.slug = newSlug;
	restampNestedIds(clone);
	return clone;
}

export function cloneImageDocForCopy(source: ImageData, newSlug: string, newId: string): ImageData {
	const clone = cloneDeep(source);
	clone.id = newId;
	clone.slug = newSlug;
	// Only one image can be the project thumbnail, so the copy never inherits the role.
	delete clone.thumbnail;
	restampNestedIds(clone);
	return clone;
}

export function cloneSceneRefForCopy(source: SceneRef, newSlug: string, newId: string): SceneRef {
	const clone = cloneDeep(source);
	clone.id = newId;
	clone.slug = newSlug;
	return clone;
}

export function cloneImageRefForCopy(source: ImageRef, newSlug: string, newId: string): ImageRef {
	const clone = cloneDeep(source);
	clone.id = newId;
	clone.slug = newSlug;
	delete clone.thumbnail;
	return clone;
}
